import { useReducer } from "react";
import { useTranslation } from "react-i18next";

import { OrgRole } from "services/orbac/types";
import { Role } from "services/rbac/types";

import { getEmptySectionsErrors, privilegesReducer } from "./privilegesReducer";
import {
  DatabasePrivileges,
  PrivilegesState,
  PrivilegesStateWithErrors,
} from "./types";
import { getPrivilegesFromRole } from "./utils";

const getInitialState = () => {
  return {
    engine: [{ toAssign: [], toDeny: [], objects: [] }],
    database: [{ toAssign: [], toDeny: [], objects: [] }],
    account: [{ toAssign: [], toDeny: [], objects: [] }],
    view: [{ toAssign: [], toDeny: [], objects: [] }],
    schema: [{ toAssign: [], toDeny: [], objects: [] }],
    table: [{ toAssign: [], toDeny: [], objects: [] }],
    organization: [{ toAssign: [], toDeny: [], objects: [] }],
    login: [{ toAssign: [], toDeny: [], objects: [] }],
    "service account": [{ toAssign: [], toDeny: [], objects: [] }],
    role: [{ toAssign: [], toDeny: [], objects: [] }],
    "network policy": [{ toAssign: [], toDeny: [], objects: [] }],
    databasePrivileges: {} as DatabasePrivileges,
    permissionErrors: {
      table: [],
      view: [],
      schema: [],
    },
  };
};

type Type =
  | "account"
  | "engine"
  | "database"
  | "view"
  | "schema"
  | "table"
  | "login"
  | "network policy"
  | "organization"
  | "service account"
  | "role";

export const usePrivileges = (initialData: PrivilegesState | null) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(
    privilegesReducer,
    (initialData || getInitialState()) as PrivilegesStateWithErrors
  );

  const { databaseLevelInitialState, databaseLevelState } = Object.values<
    PrivilegesState & { initialPrivileges: PrivilegesState }
  >(state.databasePrivileges).reduce<{
    databaseLevelState: {
      schema: PrivilegesState["schema"];
      table: PrivilegesState["table"];
      view: PrivilegesState["view"];
    };
    databaseLevelInitialState: {
      schema: PrivilegesState["schema"];
      table: PrivilegesState["table"];
      view: PrivilegesState["view"];
    };
  }>(
    (acc, state) => {
      acc.databaseLevelInitialState.table =
        acc.databaseLevelInitialState.table.concat(
          state.initialPrivileges.table
        );
      acc.databaseLevelInitialState.schema =
        acc.databaseLevelInitialState.schema.concat(
          state.initialPrivileges.schema
        );
      acc.databaseLevelInitialState.view =
        acc.databaseLevelInitialState.view.concat(state.initialPrivileges.view);

      acc.databaseLevelState.table = acc.databaseLevelState.table.concat(
        state.table
      );
      acc.databaseLevelState.schema = acc.databaseLevelState.schema.concat(
        state.schema
      );
      acc.databaseLevelState.view = acc.databaseLevelState.view.concat(
        state.view
      );
      return acc;
    },
    {
      databaseLevelInitialState: {
        schema: [] as PrivilegesState["schema"],
        table: [] as PrivilegesState["table"],
        view: [] as PrivilegesState["view"],
      },
      databaseLevelState: {
        schema: [] as PrivilegesState["schema"],
        table: [] as PrivilegesState["table"],
        view: [] as PrivilegesState["view"],
      },
    }
  );

  const getDiff = (role: Role | OrgRole, forceDatabaseLevel?: boolean) => {
    const initialState = getPrivilegesFromRole(role);
    const toAssign: { type: string; resource: string; actions: string[] }[] =
      [];
    const toRevoke: { type: string; resource: string; actions: string[] }[] =
      [];

    const diffResource = (type: Type, databaseLevel: boolean = false) => {
      const nextGroups = databaseLevel
        ? databaseLevelState[type as keyof typeof databaseLevelState]
        : state[type];
      const previousGroups = databaseLevel
        ? databaseLevelInitialState[
            type as keyof typeof databaseLevelInitialState
          ]
        : initialState[type];

      const groupByResource = (
        groups: { toAssign: string[]; toDeny: string[]; objects: any[] }[]
      ) => {
        return groups.reduce<
          Record<string, { toAssign: string[]; resource: any }>
        >((acc, group) => {
          const { objects, toAssign } = group;
          if (!toAssign.length) {
            return acc;
          }
          for (const resource of objects) {
            const key: string = resource.catalogName
              ? resource.catalogName + "/" + resource.name
              : resource;
            if (acc[key]) {
              acc[key] = {
                toAssign: [...new Set([...toAssign, ...acc[key].toAssign])],
                resource,
              };
            } else {
              acc[key] = { toAssign, resource };
            }
          }
          return acc;
        }, {});
      };

      const previousPrivilegesByResource = groupByResource(previousGroups);
      const nextPrivilegesByResource = groupByResource(nextGroups);

      const resources = Object.keys({
        ...previousPrivilegesByResource,
        ...nextPrivilegesByResource,
      });

      for (const key of resources) {
        if (
          previousPrivilegesByResource[key] &&
          !nextPrivilegesByResource[key] &&
          previousPrivilegesByResource[key].toAssign.length
        ) {
          toRevoke.push({
            type,
            resource: previousPrivilegesByResource[key].resource,
            actions: previousPrivilegesByResource[key].toAssign,
          });
        }

        if (
          !previousPrivilegesByResource[key] &&
          nextPrivilegesByResource[key] &&
          nextPrivilegesByResource[key].toAssign.length
        ) {
          toAssign.push({
            type,
            resource: nextPrivilegesByResource[key].resource,
            actions: nextPrivilegesByResource[key].toAssign,
          });
        }

        if (
          previousPrivilegesByResource[key] &&
          nextPrivilegesByResource[key]
        ) {
          const oldPrivileges =
            previousPrivilegesByResource[key].toAssign || [];
          const newPrivileges = nextPrivilegesByResource[key].toAssign || [];

          const resource = nextPrivilegesByResource[key].resource;

          const keys: string[] = Array.from(
            new Set([...oldPrivileges, ...newPrivileges])
          );

          const toAssignActions: string[] = [];
          const toRevokeActions: string[] = [];

          for (const privilege of keys) {
            if (
              oldPrivileges.includes(privilege) &&
              !newPrivileges.includes(privilege)
            ) {
              toRevokeActions.push(privilege);
            }
            if (
              !oldPrivileges.includes(privilege) &&
              newPrivileges.includes(privilege)
            ) {
              toAssignActions.push(privilege);
            }
          }

          if (toAssignActions.length) {
            toAssign.push({
              type,
              resource,
              actions: toAssignActions,
            });
          }
          if (toRevokeActions.length) {
            toRevoke.push({
              type,
              resource,
              actions: toRevokeActions,
            });
          }
        }
      }
    };

    diffResource("account");
    diffResource("organization");
    diffResource("login");
    diffResource("service account");
    diffResource("network policy");
    diffResource("role");
    diffResource("engine");
    diffResource("database");

    diffResource("schema", !forceDatabaseLevel);
    diffResource("table", !forceDatabaseLevel);
    diffResource("view", !forceDatabaseLevel);

    return {
      toAssign,
      toRevoke,
    };
  };

  const validateEmptySections = (options?: {
    role?: OrgRole | Role;
    catalogName?: string;
  }) => {
    if (options?.role) {
      const initialState = getPrivilegesFromRole(options.role);
      return getEmptySectionsErrors(t, state, initialState);
    }
    return getEmptySectionsErrors(
      t,
      state,
      getInitialState(),
      options?.catalogName
    );
  };

  return {
    state,
    dispatch,
    getDiff,
    validateEmptySections,
  };
};
